import { Injectable } from "@angular/core";
import semver from "semver";
import { CHANGELOG_ADVANCED_PAYLOAD_V146 } from "src/app/components/ui/changelog/changelog-advanced-146.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V144 } from "src/app/components/ui/changelog/changelog-advanced-144.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V143 } from "src/app/components/ui/changelog/changelog-advanced-143.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V143_37 } from "src/app/components/ui/changelog/changelog-advanced-143_37.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V143_42 } from "src/app/components/ui/changelog/changelog-advanced-143_42.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V143_46 } from "src/app/components/ui/changelog/changelog-advanced-143_46.payload";
import { CHANGELOG_ADVANCED_PAYLOAD_V143_75 } from "src/app/components/ui/changelog/changelog-advanced-143_75.payload";
import type { ChangelogAdvancedPayload } from "src/app/components/ui/changelog/changelog-advanced.model";
import {
  getCurrentSoftwareProductVersion,
  SoftwareProduct
} from "src/app/support/product";

export interface ChangelogHistoryEntry {
  product: SoftwareProduct;
  sha: string;
  committedAt: number;
  subject: string;
  version: string | null;
  bullets: string[];
  advanced?: ChangelogAdvancedPayload;
}

const CHANGELOG_HISTORY_SOURCE: ChangelogHistoryEntry[] = [
  {
    product: SoftwareProduct.PMS,
    sha: "05929d1fc7929bc46b8e9278ef273f117b71c889",
    committedAt: 1793792032,
    subject:
      "fix(SNT-12449): pdf.js worker passato esplicitamente nelle miniature PDF",
    version: "1.47.3",
    bullets: [
      "Anteprime PDF: risolto un errore tecnico che si verificava in background durante la generazione delle anteprime dei documenti PDF"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b33a2a9278e118e99bc60a96eba145d537e9a744",
    committedAt: 1793620260,
    subject: "feat(SNT-12351): aggiunge filtro per età",
    version: "1.47.1",
    bullets: [
      "Segmentazioni: aggiunto il filtro per età del paziente, per selezionare i pazienti che hanno almeno il numero di anni indicato"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000003",
    committedAt: 1793288055,
    subject: "feat(SNT-1): changelog v1.47.0",
    version: "1.47.0",
    bullets: [
      "Abbiamo introdotto diverse novità e miglioramenti per rendere il lavoro quotidiano più semplice e fluido, ottimizzando la gestione delle fatture, delle prestazioni, degli ordini e della documentazione clinica.",
      "La gestione delle prestazioni a tariffario è stata migliorata: ora è possibile cercarle anche per descrizione e collegarle ai farmaci. Inoltre, la percentuale del medico in conto viene recuperata direttamente dal tariffario, evitando che venga impostata automaticamente al 100%.",
      "Sono state introdotte migliorie anche nella gestione delle fatture, dalla compilazione alla modifica e all'esportazione. È ora possibile aggiornare il codice fiscale anche su preventivi già creati, e il codice fiscale viene calcolato automaticamente a partire dal preventivo.",
      "Abbiamo inoltre corretto alcuni problemi relativi all'emissione delle note di credito e aggiornato la sezione dedicata alla creazione delle fatture di riepilogo. L'indicatore delle prestazioni da fatturare è ora visibile anche per i pazienti deceduti.",
      "Anche le statistiche del fatturato sono più accurate: gli acconti non vengono più conteggiati due volte quando il saldo viene registrato.",
      "La gestione delle schede cliniche è stata migliorata: la firma viene ora associata agli autori corretti, anziché all'utente attualmente collegato.",
      "Le modifiche apportate sono consultabili senza dover aggiornare continuamente la pagina, rendendo la consultazione più immediata.",
      "È stata inoltre migliorata la possibilità di inviare il diario clinico tramite l'apposita icona dell'aeroplanino, sia in PMS sia in Rocket.",
      "Anche le email destinate al medico referente sono state corrette: il testo utilizzato è ora quello appropriato, anziché quello previsto per il proprietario.",
      "Sono stati introdotti diversi miglioramenti nella gestione degli ordini IDEXX VC+. La sincronizzazione degli ordini esterni funziona correttamente anche quando il campo del tecnico è vuoto, mentre è stata migliorata la gestione degli errori provenienti da VetConnect, che ora risultano visibili.",
      'Abbiamo inoltre risolto i problemi legati alla creazione di work order orfani e aggiunto la visualizzazione del messaggio "Metti in conto" dopo la conferma di un ordine.',
      "Per i test IDEXX VC+, vengono ora gestiti correttamente anche i valori massimi quando l'API non restituisce il limite superiore.",
      "È stata migliorata la gestione degli eventi relativi agli invii al Sistema Tessera Sanitaria, con correzioni anche per alcuni casi residui.",
      "Per la fatturazione elettronica, sono stati corretti i contatori degli invii dopo l'aggiornamento degli esiti SDI, così da garantire una visualizzazione più coerente dello stato delle trasmissioni.",
      "Il viewer OHIF è stato aggiornato alla versione 3.9 o successiva, riducendo la dipendenza da SharedArrayBuffer.",
      "Sono stati inoltre risolti alcuni problemi specifici: gli esami sono nuovamente visibili in PMS per Ambulatorio Concordia e la gestione di analisi e referti relativi ai conigli è stata corretta per Clodiavet.",
      "Dopo la riconciliazione degli ordini, le schede ViLMA riportano correttamente i dati del cliente e del paziente.",
      'Infine, la flowboard è stata aggiornata con l\'opzione "Fattura in seguito", per offrire una maggiore flessibilità nella gestione delle attività.'
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "52f96eea8549e4ab5742aee847649578ae04ff80",
    committedAt: 1793284736,
    subject:
      "fix(SNT-12345): evita le raffiche di GET /billing/patients/{id}/books nel dettaglio cliente",
    version: "1.46.14",
    bullets: [
      "Migliorate le prestazioni di conti e fatture: il dettaglio cliente non ricarica più i conti di tutti gli animali a ogni operazione di fatturazione"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1d04911d93f2ca8f0411557e51a73540be3ae71c",
    committedAt: 1793264596,
    subject:
      "fix(SNT-12332): avvia i task di TasksService.execute anche se l'app non è stabile",
    version: "1.46.13",
    bullets: [
      "Corretto il caricamento di un documento dalla scheda di pazienti, clienti e ricoveri, che in alcuni casi non partiva dopo la scelta del file"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "be6a4894c17a49c056e86884e5f87b20c9a7de14",
    committedAt: 1792508479,
    subject:
      "fix(SNT-12239): corregge assegnazione specie e razza a ordine Vilma",
    version: "1.46.12",
    bullets: [
      "Laboratorio ViLMA: corretta la precompilazione di specie e razza del paziente nella richiesta, anche per conigli, cavalli e altre specie",
      "Corretto il caricamento del diario clinico per i pazienti senza proprietario associato",
      "IDEXX VetConnect PLUS: corretto un errore sugli ordini di lavoro privi dei dati di integrazione"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "5f5356b3e0d2bbb934c18e2d374cfc3e67aef61c",
    committedAt: 1792152299,
    subject:
      "feat(SNT-12204): configure profis pn excluded payment methods in tenant codes",
    version: "1.46.11",
    bullets: [
      "Codici contabili PROFIS: aggiunta la scelta dei metodi di pagamento da escludere dall'export della prima nota, per registrare quegli incassi direttamente da estratto conto"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "370a9ba8183eabcdda02c05d332a9758c1235684",
    committedAt: 1792140231,
    subject: "fix(SNT-12203): scanner barcode robusto al carico del PC",
    version: "1.46.10",
    bullets: [
      "Corretto un problema per cui la lettura dei codici a barre con la pistola poteva non essere rilevata quando il computer era molto carico"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "c6f94d5a3e112467a8fd92f3783f4f83143dbe00",
    committedAt: 1792060999,
    subject:
      "fix(SNT-12185): resolve one effective shift per member in calendar",
    version: "1.46.9",
    bullets: [
      "Corretto un problema per cui in agenda i turni a settimane alterne venivano mostrati ogni settimana quando per lo stesso membro esistevano più turni ricorrenti sovrapposti",
      "Export PROFIS: aggiunti conti cliente estero UE ed Extra-UE, codice IVA estero, registro IVA elettronico, tipo operazione ritenuta e forzatura territorialità Italia",
      "Rinnovata la configurazione dei codici contabili PROFIS con etichette uniformate, valori tipici suggeriti, conti per aliquota IVA opzionali e guida integrata"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "3744ac88ee90b2ff16f5e4d856b9fae0f10a75ed",
    committedAt: 1791816730,
    subject: "fix(SNT-12139): migliora feedback errore oauth su emissione rev",
    version: "1.46.8",
    bullets: [
      "Migliorati i messaggi di errore in emissione e annullamento ricette VetInfo REV quando l'autenticazione è scaduta, con avviso dedicato per le connessioni in delega",
      "Aggiornate etichette e testo di aiuto nel modale di scelta utente VetInfo (utente REV e deleghe)"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "be68937fe60be20aad797c2a2bcc372aac685274",
    committedAt: 1791297612,
    subject:
      "fix(SNT-12040): add executed-by field to drug and product add modals",
    version: "1.46.5",
    bullets: [
      "Corretto un problema nella rettifica di magazzino per cui lotto, scadenza, prezzo e confezionamento non venivano trasmessi correttamente nel movimento",
      "Aggiunto il campo «eseguito da» nei modali di aggiunta al conto per farmaci e prodotti"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000002",
    committedAt: 1790605740,
    subject: "fix(SNT-12026): use appointment tenant in reminder notifications",
    version: "1.46.4",
    bullets: [
      "Corretto un problema per cui i promemoria degli appuntamenti potevano indicare la sede sbagliata nelle organizzazioni multi-sede"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "416a49a6d49c23a413fec879a97f20013a6ba44c",
    committedAt: 1790333075,
    subject:
      "fix(SNT-12008): raise appointment hover preview above shift badges",
    version: "1.46.3",
    bullets: [
      "Corretto un problema per cui i tag dei turni in agenda coprivano l'anteprima al passaggio del mouse sugli appuntamenti"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1790173938,
    subject: "fix(SNT-1): keep scheduler search start across daylight saving",
    version: "1.46.2",
    bullets: [
      "Corretto il caricamento in agenda degli appuntamenti ricorrenti al passaggio dall'ora legale all'ora solare"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "80af5430561682a2a06293a85256c5be8cab143b",
    committedAt: 1789549458,
    subject: "feat(SNT-1): changelog v1.46.0",
    version: "1.46.0",
    bullets: [
      "Ricerca globale ottimizzata con proprietari cliccabili e ricerca avanzata",
      "Medici esterni in Gestione utenti, piani digitali e consegna digitale referti",
      "Miglioramenti agenda: granularità scheduler, note appuntamento, durata in modifica",
      "Data di check-in in scheda clinica e miglioramenti fatturazione e preventivi",
      "Correzioni minori"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V146
  },
  {
    product: SoftwareProduct.PMS,
    sha: "db7c2dd11e802b4d3dcd18dc069900670bc6f7dd",
    committedAt: 1785491862,
    subject:
      "fix(SNT-11594): handle undefined options in member backend methods",
    version: "1.45.",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "db7c2dd11e802b4d3dcd18dc069900670bc6f7dd",
    committedAt: 1785491862,
    subject:
      "fix(SNT-11594): handle undefined options in member backend methods",
    version: "1.45.2",
    bullets: [
      "Corretto un problema che in alcuni casi impediva il caricamento dei membri dello staff nei campi di selezione"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "00dd7c1bd4b6ea1852ed85b8d651c9035e4f9563",
    committedAt: 1785404894,
    subject: "feat(SNT-1): changelog v1.45.1",
    version: "1.45.1",
    bullets: [
      "Corretto un problema che impediva di ricoverare un paziente con visita in corso"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "09ddb2dc0079a84cf21695686e663505251dfdeb",
    committedAt: 1785251112,
    subject: "feat(SNT-1): changelog v1.45.0",
    version: "1.45.0",
    bullets: [
      "Completamento automatico dei promemoria al check-in, con conversioni, statistiche e menu analytics",
      "Aggiunto datepicker e filtri per data programmata nella tabella e nell'elenco promemoria",
      "Migrata la pagina di modifica dei template promemoria",
      "Fattura di riepilogo parziale: card migliorata, scroll con molti pazienti, warning se nessuno selezionato e lista prestazioni in notula",
      "Aggiunto filtro per motivo di presentazione",
      "Filtro sulle fatture stornate e netto delle note di credito collegate nel da pagare",
      "Esportazione fatture Profis e chiavi piano dei conti",
      "Selezione e merge PDF nella stampa della scheda SOAP",
      "Migliorata la visualizzazione dei turni con etichette principali e overflow",
      "Aggiunta quantità nel riconfezionamento",
      "Corretta l'esportazione del tariffario con le righe selezionate",
      "Corretta la valorizzazione del cliente in creazione appuntamento",
      "Migliorato l'ordine alfabetico dello staff nello scheduler",
      "Esclusi gli utenti di sistema dagli elenchi di creazione risorse",
      "Gestione della nazione nel profilo fiscale",
      "Corretto l'invio notifica dopo creazione allegato",
      "Migliorata la gestione tenant (redirect detail/edit e theme v2)",
      "Migliorata la gestione della configurazione diagnostica e della banda quota scaduta",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b5e0e33684f30abece7aad4434a5e97dc22c9088",
    committedAt: 1784539493,
    subject: "feat(SNT-1): changelog v1.44.21",
    version: "1.44.21",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b5e0e33684f30abece7aad4434a5e97dc22c9088",
    committedAt: 1784539493,
    subject: "feat(SNT-1): changelog v1.44.16",
    version: "1.44.16",
    bullets: [
      "Corretto un problema che impediva di modificare la data di fine ricorrenza di un appuntamento"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "fae4aeca72b82be3b6db392e902533546f88e1c0",
    committedAt: 1784291501,
    subject: "feat(SNT-1): changelog v1.44.13",
    version: "1.44.13",
    bullets: [
      "Migliorato il caricamento di ticket e appuntamenti nella board",
      "Migliorato il layout del form appuntamento con note nel dettaglio principale",
      "Migliorato il check-in con avviso quando il paziente risulta già in struttura",
      "Migliorata la gestione e il caricamento dei profili fiscali",
      "Migliorate le prestazioni generali con cache delle richieste",
      "Migliorata la visualizzazione dei grafici nelle statistiche",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "3779d6977d574d5ef68f1cf73fd8120cb7e1e645",
    committedAt: 1784062860,
    subject: "feat(SNT-1): changelog v1.44.10",
    version: "1.44.10",
    bullets: [
      "Rinnovata la gestione degli appuntamenti in agenda con apertura del form nel pannello laterale (Sidespace)",
      "Aggiunto avviso nel form appuntamento quando data e ora di inizio sono nel passato",
      "Migliorata la selezione del paziente con etichetta compatta (nome, specie ed età)",
      "Migliorata la dashboard statistiche con scrolling virtuale e nuove strutture dati",
      "Migliorate le anteprime PDF dei documenti con caricamento lazy e cache di sessione",
      "Migliorata la selezione degli orari di fine appuntamento con opzioni a step di 15 minuti e indicazione della durata",
      "Aggiunto zoom e regolazione larghezza barre nei grafici delle statistiche",
      "Migliorata la validazione delle date in agenda",
      "Migliorata la gestione dell'indirizzo in anagrafica clienti con validazione al cambio",
      "Aggiunte informazioni tooltip nello scheduler",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "75b33624a67174a43847f7612230d7a1b7e70cb6",
    committedAt: 1783036800,
    subject: "feat(SNT-1): changelog v1.44.0",
    version: "1.44.0",
    bullets: [
      "Tab verticali e azioni compatte nelle pagine di dettaglio",
      "Squadratura fattura, tariffario v2 e IVA svizzera",
      "Esportazione Wolters Kluwer e rinnovo gestione ruoli",
      "Miglioramenti agenda, clinica, magazzino e correzioni minori"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V144
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780920000,
    subject: "",
    version: "1.43.75",
    bullets: [
      "Aggiunte le Aree con PIN",
      "Aggiunto l'onboarding per i membri dello staff",
      "Corretti alcuni problemi in Agenda",
      "Correzioni minori"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V143_75
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780910000,
    subject: "",
    version: "1.43.73",
    bullets: [
      "Migliorato il popup di accettazione privacy e disclaimer legale per Snoots Connector for AI (MCP)",
      "Migliorata la leggibilità del disclaimer nel tema scuro e la dimensione dei titoli di sezione",
      "Il pulsante di conferma è ora «Accetta e installa», con indicazione se manca l'accettazione dei termini",
      "Migliorata l'accessibilità dei dialoghi modali bloccanti (lettura screen reader e focus da tastiera)"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780900000,
    subject: "",
    version: "1.43.72",
    bullets: [
      "Introdotto Snoots Connector for AI (MCP) per collegare assistenti AI ai dati gestionali",
      "Richiesta accettazione del disclaimer legale e privacy prima dell'installazione del connettore MCP",
      "Dettaglio installazione MCP con URL di connessione, guida alla configurazione in Claude e informativa GDPR/AI Act",
      "Aggiunte statistiche di utilizzo MCP con riepilogo per tool e agente",
      "Dopo l'installazione di un componente aggiuntivo si apre automaticamente il dettaglio dell'installazione",
      "Corretto un problema che impediva l'installazione di un componente aggiuntivo dalla pagina di dettaglio"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780900000,
    subject: "",
    version: "1.43.70",
    bullets: [
      "Aggiunta la gestione del reparto negli appuntamenti, con filtro, colonna in vista elenco e badge in agenda",
      "Aggiunto il pulsante per mostrare la password nelle schermate di accesso, registrazione e recupero credenziali",
      "Migliorata la gestione degli errori e del ricaricamento dell'agenda"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780800000,
    subject: "",
    version: "1.43.69",
    bullets: [
      "Aggiunta la gestione delle specializzazioni dello staff",
      "Migliorati i filtri dell'agenda per qualifica, reparto e specializzazione",
      "Migliorata la selezione dello staff nella creazione degli appuntamenti con indicazione di turni e reparti",
      "I turni possono essere configurati e visualizzati per reparto"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780700000,
    subject: "",
    version: "1.43.68",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780700000,
    subject: "",
    version: "1.43.64",
    bullets: [
      "Miglioramenti nella visualizzazione dei turni e reparti in agenda"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780700000,
    subject: "",
    version: "1.43.62",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780600000,
    subject: "",
    version: "1.43.61",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780500000,
    subject: "",
    version: "1.43.60",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780500000,
    subject: "",
    version: "1.43.56",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780500000,
    subject: "",
    version: "1.43.55",
    bullets: [
      "Migliorata l'esperienza mobile dell'agenda con apertura predefinita in vista giornaliera",
      "Rinnovato il layout dello scheduler per gestire meglio toolbar, filtri e contenuto",
      "Il pannello filtri resta disponibile quando si passa alla vista elenco dell'agenda"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780500000,
    subject: "",
    version: "1.43.54",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780500000,
    subject: "",
    version: "1.43.53",
    bullets: [
      "Corretto un problema che impediva la selezione della data corretta nei promemoria",
      "Corretto un problema che impediva il caricamento del codice AIC dopo la selezione di un farmaco",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780400000,
    subject: "",
    version: "1.43.52",
    bullets: [
      "Corretto un problema che impediva l'aggiornamento automatico dei documenti di un paziente dopo la creazione",
      "Corretto un problema che in alcuni casi impediva di selezionare gli allegati da inviare di un paziente",
      "Corretto un problema che impediva la corretta visualizzazione dei membri in turno nello scheduler"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780300000,
    subject: "",
    version: "1.43.46",
    bullets: [
      "Nuova agenda, vista elenco con filtro periodo e toolbar rinnovata"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V143_46
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780200000,
    subject: "",
    version: "1.43.42",
    bullets: ["Rimosso il limite di stampa dei documenti"],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V143_42
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000001,
    subject: "",
    version: "1.43.37",
    bullets: [
      "Acconti, notule numerate, assegna a dal preventivo e nuove scorciatoie"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V143_37
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.33",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.31",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.30",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.29",
    bullets: [
      "Corretto un problema che impediva la visualizzazione del metodo di pagamento nella tabella delle fatture"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.28",
    bullets: ["Migliorato il caricamento del conto paziente"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.27",
    bullets: ["Migliorato il caricamento del diario clinico"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.26",
    bullets: [
      "Corretto un problema che in alcuni casi impediva la visualizzazione dello stato di caricaricamento del diario clinico"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.25",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.21",
    bullets: [
      "Corretto un problema nel caricamento dei turni per l'agenda multi tenant"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.18",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.17",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.16",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.15",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.14",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.12",
    bullets: ["Corretto un problema che impediva la stampa su tablet"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.11",
    bullets: [
      "Gestiti vincoli per emissione fattura",
      "Corretto ordine alfabetico staff in creazione appuntamento",
      "Gestito refresh flowboard dopo checkout",
      "Gestita chiusura popup quando si copiano dati",
      "Aggiunta gestione di più risultati in IDEXX",
      "Gestita creazione checkin con dati minimi",
      "Gestito cambio prestazione in vaccinazione con modello vaccino",
      "Gestite traduzioni in inglese della stampa riepilogo fatture insolute",
      "Corretto copy delete massivo notule",
      "Ripristinate azioni di navigazione nel calendar",
      "Corretto un problema nella gestione dei turni nello scheduler",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.7",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.ROCKET,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "3.18.1",
    bullets: ["Correzioni minori"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "",
    version: "1.43.6",
    bullets: ["Corretto un problema nella gestione dei tag eliminati"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0000000000000000000000000000000000000001",
    committedAt: 1780000000,
    subject: "feat(SNT-1): changelog v1.43.0 advanced release notes",
    version: "1.43.0",
    bullets: [
      "Agenda rinnovata, ruoli, fatturazione guidata e nuovi connettori",
      "Novità su scheduler, utenti, fatturazione e usabilità"
    ],
    advanced: CHANGELOG_ADVANCED_PAYLOAD_V143
  },
  {
    product: SoftwareProduct.PMS,
    sha: "819d094b6f9c22b3eae5dacc3cc86011af644e13",
    committedAt: 1774435430,
    subject:
      "fix(SNT-10341): resolve appointment time issue during modification and update changelog entries",
    version: "1.42.6",
    bullets: [
      "Corretto un problema sull|orario durante la modifica di un appuntamento"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a2e974c8a6ed2f11e423e6ce1fa1f267a46733aa",
    committedAt: 1774362597,
    subject:
      "fix(SNT-1): enhance tag search functionality with pagination and improved data handling",
    version: "1.42.5",
    bullets: [
      "Corretto un problema che in alcuni casi impediva il caricamento dei tag nei campi di selezione"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "f52f1d78d61f51b519034a48d7351951a75789ae",
    committedAt: 1770893846,
    subject: "feat(SNT-1): changelog",
    version: "1.42.1",
    bullets: [
      "Migliorata la visualizzazione delle consegne con indicazione del canale e dell’oggetto del messaggio quando disponibile",
      "Ora nella statistiche relative al fatturato viene impostato il filtro per profilo fiscale utilizzando quello impostato come default",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "58445688022a7bfd23ace7c14df8e0354dba3909",
    committedAt: 1768496993,
    subject: "feat(SNT-1): changelog",
    version: "1.41.6",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "031a1c9e18504498b7f982fb50a2b314a50d781d",
    committedAt: 1767627639,
    subject: "feat(SNT-1): changelog",
    version: "1.41.5",
    bullets: [
      "Corretto un problema nella creazione del ricovero dal dettaglio paziente"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "2b9465f5ae11aea0b1dac4efccb47d33a87077ee",
    committedAt: 1767095807,
    subject: "fix(SNT-1): changelog",
    version: "1.41.0",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "961acc41fa1a9d9e4204712476b62249e929494d",
    committedAt: 1767094364,
    subject: "feat(SNT-1): changelog",
    version: "1.40.22",
    bullets: [
      "Amministrazione e fatturazione",
      "Aggiunto il modulo dei clienti insoluti",
      "Fatture passive: introdotta la possibilita di stampa e corretta la visualizzazione della pagina pagamenti",
      "Ricerca avanzata: ottimizzata la ricerca per numero all'interno del modulo fatture",
      "Esportazioni: Risolto un problema che impediva la corretta esportazione dei dati relativi agli studi",
      "Diario clinico e anagrafiche contatti",
      "Diario clinico: aggiunto un nuovo filtro per Medico per facilitare la consultazione delle visite",
      "Contatti: risolto problema nell|unione delle anagrafiche di due contatti",
      "Anagrafica: migliorato l|algoritmo di calcolo dell|eta dei pazienti per una maggiore precisione cronologica",
      "Stampe: corretto un bug nella stampa degli allegati del diario clinico",
      "Agenda e Turni",
      "Gestione Turni: risolto il problema dei turni sovrapposti e corretta l|anteprima di stampa dei turni settimanali/mensili",
      "Task e Operativita",
      "Ricerca: aggiunta la ricerca per cliente all|interno della gestione task",
      "Visualizzazione: risolti i problemi di rendering e visualizzazione della lista task",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "96ce51b9a9d95c7cf5299bda02cad4f242ca74da",
    committedAt: 1766155200,
    subject: "feat(SNT-1): changelog",
    version: "1.40.22",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0e31d6817dfa4d08f4251ac7d5fcfdba57d469c1",
    committedAt: 1765887884,
    subject: "feat(SNT-1): changelog",
    version: "1.40.19",
    bullets: [
      "Aggiunte e migliorate alcune funzionalita nelle statistiche per prestazioni/prodotto/farmaco"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "6061e0c85f32daa292f2112970ae7855d587e4a6",
    committedAt: 1765820177,
    subject: "fix(SNT-1): changelog",
    version: "1.40.17",
    bullets: [
      "Corretto un problema nella visualizzazione delle statistiche dei contatti attivi",
      "Corretto un problema nella visualizzazione delle statistiche del fatturato per staff"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "9e54d11d2aa3831c49ac92647e45b34ba837033c",
    committedAt: 1765814406,
    subject: "fix(SNT-1): changelog",
    version: "1.40.12",
    bullets: [
      "Corretto un problema nella visualizzazione delle statistiche dei contatti attivi"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "9338f1911788c5ce05837e59f0146ddbb7e6c985",
    committedAt: 1765366676,
    subject: "feat(SNT-1): changelog",
    version: "1.40.9",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "7b896d09aa240827d6b8cfcfc1d4898c0fd69bdd",
    committedAt: 1764018266,
    subject: "feat(SNT-1): changelog",
    version: "1.39.27",
    bullets: [
      "Aggiunta la possibilità di visualizzare IVA e ENPAV nella statistiche fatturato",
      "Aggiunta la possibilità di ricercare all|interno del diario clinico",
      "Aggiunta la possibilità di stampare le immagini allegate agli esami di laboratorio",
      "Aggiunto un filtro nell|elenco fatture per cliente",
      "Migliorata la UI nell|inserimento del paziente",
      "Corretto un problema che impediva l'invio dei documenti nel diario clinico",
      "Corretto un problema che in alcuni casi impediva di selezionare tutti gli studi",
      "Corretto un problema che in alcuni casi impediva di crare il conto del paziente",
      "Correzzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "784ae64f15ecd6624971e36524febd1d632b89bd",
    committedAt: 1763419972,
    subject: "feat(SNT-1): changelog",
    version: "1.39.25",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e2e7e15408143720e01b9e5b514d51de9929daab",
    committedAt: 1762813865,
    subject: "feat(SNT-1): changelog",
    version: "1.39.24",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "4360dbe5375caa4a8114886b19151896dbf03c53",
    committedAt: 1761926764,
    subject: "feat(SNT-1): changelog",
    version: "1.39.23",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "679a3fccfc254d518e28bfd58ae4edda25211b19",
    committedAt: 1761563097,
    subject: "feat(SNT-1): changelog",
    version: "1.39.21",
    bullets: ["Migliorata la gestione del pin e lo sblocco della sessione"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "9d32e77d7965a8be4a459e3c2a5cdb38c73e455c",
    committedAt: 1761514700,
    subject: "feat(SNT-1): changelog",
    version: "1.39.19",
    bullets: [
      "Migliorati i tab, ora sono sempre tutti visibili",
      "Corretto un problema nelle statistiche con gli importi in negativo",
      "Aggiunta possibilità di retrodatare i vaccini",
      "Corretto un problema nella pagina di dettaglio delle fatture passive",
      "Migliorate le performance in export fatture",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "afde601d0e70c20479d4f0942228b0935958ea3f",
    committedAt: 1760102550,
    subject: "feat(SNT-1): changelog",
    version: "1.39.12",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "587b798e6075c3b2a31f16b9ae0f8c2ac9061b50",
    committedAt: 1760091947,
    subject: "fix(SNT-1): flowboard update board ticket",
    version: "1.39.11",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "ef18adcb63b0ffcfdd64ff013cd9286256d39595",
    committedAt: 1759846766,
    subject: "feat(SNT-1): changelog",
    version: "1.39.4",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "8c0a312fe5775fca441debda06365ffb2a08339d",
    committedAt: 1759825773,
    subject: "fix(SNT-9303): remove fixed height soap checklist",
    version: "1.39.3",
    bullets: [
      "Corretto un problema nella visualizzazione delle checklist nelle schede SOAP"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "50fb881a028ff71d7e65268a7fb83539ab73512c",
    committedAt: 1759738292,
    subject: "feat(SNT-1): disabled kpi",
    version: "1.39.0",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "73f9d4116288db2a5c52409a476d1a263efac8b5",
    committedAt: 1759691968,
    subject: "feat(SNT-1): changelog",
    version: "1.38.16",
    bullets: [
      "Aggiunti i KPI",
      "Aggiunta la possibilità di creare un collegamento ad un contato o ad un paziente in un task",
      "Aggiunta la possibilità di stampare massivamente le fatture",
      "Aggiunta la possibilità di filtrare gli elementi presenti nella flowboard",
      "Migliorata la visiilità delle ferie in agenda",
      "Aggiunta la possibilità di cercare i contatti tramite numero identificativo",
      "Corretti alcuni problemi relativi ai ricoveri",
      "Apportati alcuni miglioramenti alle statistiche",
      "Corretti alcuni problemi nella visualizzazione dei turni in agenda",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "033d42e80377952a0830181ec2ceb411bf951706",
    committedAt: 1758725080,
    subject: "fix(SNT-9253): enable send/print for all pdf attachments",
    version: "1.38.14",
    bullets: [
      "Corretto un problema che in alcuni casi impediva di inviare e stampare i documenti allegati al paziente"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "dc7fd49fe2f08648115aa977fb15b68d9980bbfb",
    committedAt: 1758544018,
    subject: "fix(SNT-9240): agenda appointments print with filters",
    version: "1.38.13",
    bullets: [
      "Ora la stampa degli appuntamenti in agenda è allineata ai filtri in uso"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "93ede1fa70e35e9a900e4419a302eeb10b66c951",
    committedAt: 1757574512,
    subject: "feat(SNT-1): changelog",
    version: "1.38.7",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "685598a624ffa8bd16e31faaf4c699442c49ea09",
    committedAt: 1757421487,
    subject: "feat(SNT-1): changelog",
    version: "1.38.6",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "7526b749eae05d59165b3b24aa1ad0b3d1d7b42a",
    committedAt: 1756192365,
    subject: "feat(SNT-1): changelog",
    version: "1.38.2",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "55ce3dd9ac6f917d0c91746be19cffb088c4f837",
    committedAt: 1755466443,
    subject: "feat(SNT-1): changelog",
    version: "1.37.5",
    bullets: [
      "Aggiunti i filtri nella sezione dei promemoria inseriti",
      "Aggiunto il dettaglio di una scheda clinica di tipo laboratorio",
      "Revisione delle statistiche",
      "Aggiunta la possibilità di confrontare nello statistiche lo stesso periodo su più anni",
      "Nello scheduler ora è possibile attivare la visualizzazione solo per turni dello staff",
      "Aggiunto il codice cliente nel file di esportazione",
      "Rimossi i conti paziente datati sul conto di cassa aperto",
      "Aggiunto un avviso nel caso in cui un paziente abbia una razza non prevista in Snoots PMS",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e7af89e9553aec04fe5cf86d84a9c9be634efde4",
    committedAt: 1755090497,
    subject: "feat(SNT-1): changelog",
    version: "1.37.4",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "997b859d3fa4d5058de427cfabac87d312ae7d38",
    committedAt: 1754917259,
    subject: "feat(SNT-1): changelog",
    version: "1.37.3",
    bullets: [
      "Aggiunto un avviso nel caso in cui la razza di un paziente non sia tra quelle previste in Snoots PMS",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b5f5f9ceaaa4455f480a3c6f541924f77e2fed42",
    committedAt: 1752076092,
    subject: "feat(SNT-1): changelog",
    version: "1.36.7",
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "de01c38120dd979d96b6785129692cac783c93ca",
    committedAt: 1752065723,
    subject: "feat(SNT-1): changelog",
    version: "1.36.6",
    bullets: [
      "Corretto un problema che in alcuni casi mostrava orari errati negli appuntamenti della flowboard",
      "Corretto un problema che posizionava in maniera errata il popup per la creazione di un nuovo appuntamento"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "21bdb93161decad1aa66d2968597dec9d6b366af",
    committedAt: 1751983891,
    subject: "feat(SNT-1): changelog",
    version: "1.36.5",
    bullets: [
      "Corretto un problema che impediva di navigare alla data odierna nell|agenda",
      "Corretto un problema che impediva la visualizzazione delle note nei ticket"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "be26ae0d317b583feca084b78614256e2e830fe9",
    committedAt: 1751891249,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Aggiunta l|azione condividi nel dettaglio della ricetta",
      "Aggiunto il filtro per motivo di scarico nei movimenti di uno storage",
      "Aggiunta la data di nascita nella modifica di un contatto",
      "Aggiunta la possibilità di non visualizzare i nomi del personale non medico che ha effettuato le prestazioni",
      "Ora sono visibili con maggior evidenza gli appuntamenti annullati nella dashboard e nella flowboard",
      "Aggiunta la stampa degli appuntamenti in agenda",
      "Corretto un problema che in alcuni casi impediva il caricamento del prezzo di un farmaco inserito tramite scansione",
      "Corretto un problema che in alcuni casi impediva l|aggiornamento di un preventivo dopo la modifica del prezzo massimo di un elemento",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "91d2de39b0f00bb42f053c78e76698c7242038dd",
    committedAt: 1750864728,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "02cbbab0b03b93703f10c380916add69ad6844c0",
    committedAt: 1750427663,
    subject: "feat(SNT-2): update changelog",
    version: null,
    bullets: [
      "Aggiunte le statistiche per metodo di pagamento",
      "Aggiunta la possibilità di aggiungere al conto paziente dalla ricetta",
      "Aggiunta la possibilità di stampare massivamente le fatture di un cliente",
      "Agginuta la categoria nell'estrazione di prestazioni",
      "Ora l'agenda mostra gli appuntamenti in formato tabellare",
      "Aggiunta la possibilità di stampare la ricevuta degli incassi di una fattura",
      "Aggiunta la possibilità di inviare la ricevuta degli incassi di una fattura via email",
      "Ora i tab dei clienti/pazienti mostrano il titolo più chiaramente",
      "Nella vista risorse dell'agenda vengono mostrati di default lo staff in turno",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "7ca1ec7a41b44143af17c7c555f410e70540dd79",
    committedAt: 1750148232,
    subject: "fix(SNT-8761): sse guard",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "bf411251f3928934b72f5ff661ff392f175c7e2d",
    committedAt: 1749721191,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Corretto un problema che in alcuni casi impediva un corretto posizionamento dell|agenda",
      "Corretto un problema nel calcolo delle statistiche delle campagne"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "f05c4be6f7942f917f5d6a46efe578b54dc3a753",
    committedAt: 1748376275,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Aggiunte la possibilità di creare campagne",
      "Aggiunta la possibilità di creare liste di contatti secondo criteria predefiniti o personalizzati",
      "Aggiunta una colonna con il metodo di pagamento nella tabella delle fatture emesse",
      "Aggiunta la possibilità di includere nella stampa del conto paziente solo le prestazioni non aggiunte a nessuna fattura",
      "Aggiunta la possibilità di creare un nuovo documento direttamente dal dettaglio preventivo",
      "Aggiunta l'informazione se il paziente è sterilizzato nella pagina di dettaglio",
      "Aggiunta la possibilità di emettere fattura anche per un paziente deceduto",
      "Aggiunta la possibilità di filtrare i ricoveri per veterinario referente",
      "Corretto un problema nell'estrazione dei contatti selezionati",
      "Corretto un problema che in alcuni casi non registrava correttamete il CAP del contatto",
      "Corretto un problema che lasciava attivi le azioni STAMPA e INVIA dopo l'annullamento di una ricetta",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "69976929a807ad53dc1556fa888f84229002c59b",
    committedAt: 1748376275,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Aggiunte la possibilità di creare campagne",
      "Aggiunta la possibilità di creare liste di contatti secondo criteria predefiniti o personalizzati",
      "Aggiunta una colonna con il metodo di pagamento nella tabella delle fatture emesse",
      "Aggiunta la possibilità di includere nella stampa del conto paziente solo le prestazioni non aggiunte a nessuna fattura",
      "Aggiunta la possibilità di creare un nuovo documento direttamente dal dettaglio preventivo",
      "Aggiunta l'informazione se il paziente è sterilizzato nella pagina di dettaglio",
      "Aggiunta la possibilità di emettere fattura anche per un paziente deceduto",
      "Aggiunta la possibilità di filtrare i ricoveri per veterinario referente",
      "Corretto un problema nell'estrazione dei contatti selezionati",
      "Corretto un problema che in alcuni casi non registrava correttamete il CAP del contatto",
      "Corretto un problema che lasciava attivi le azioni STAMPA e INVIA dopo l'annullamento di una ricetta",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1aa5fd549c0d93e31cead166503eb6a8795b4265",
    committedAt: 1746799225,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "3a8e4878791bb0fc17dad43e8d3ebe611eb8f00a",
    committedAt: 1746799225,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "49097c62c8ec5312978c057bd3e028c68acbfec3",
    committedAt: 1745777863,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "E| stata aggiunta la vista per risorse nell|agenda",
      "Migliorato il modo in cui vengono rappresentati gli appuntamenti in agenda",
      "Aggiunta la possibilità di estrarre separatamente le giacenze per storage",
      "Aggiunta la possibilità di filtrare i veterinari che hanno creato una particolare degenza",
      "Inserito un avviso sulla variazione di prezzo di un farmaco",
      "Aggiunta la possibilità di modificare il nome di un articolo presente nel magazzino",
      "Rivisto l|ordine di visualizzazione di alcuni elenchi",
      "Rivisto il modo in cui vengono presentati gli articoli in magazzino durante le fasi di consegna/scarico",
      "Corretto un problema nel magazzino in cui in alcuni casi erronemanete salvava un articolo con un nome diverso da quello previsto",
      "Corretto un problema che impediva la visualizzazione degli sfondi colorati nei pdf generati durante la stampa",
      "Corretto un problema in cui in un caso particolare il sezionale di una fattura veniva rimosso",
      "Corretto un problema di layout nel lightsearch",
      "Corretto un problema nei reminder",
      "Corretti alcuni problemi inerenti le traduzioni",
      "Corretto un problema che in alcuni casi impediva di salvare la configurazione dell'account",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a42624e4940affe3b6407fdd33e3d4648672945f",
    committedAt: 1745777863,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "E| stata aggiunta la vista per risorse nell|agenda",
      "Migliorato il modo in cui vengono rappresentati gli appuntamenti in agenda",
      "Aggiunta la possibilità di estrarre separatamente le giacenze per storage",
      "Aggiunta la possibilità di filtrare i veterinari che hanno creato una particolare degenza",
      "Inserito un avviso sulla variazione di prezzo di un farmaco",
      "Aggiunta la possibilità di modificare il nome di un articolo presente nel magazzino",
      "Rivisto l|ordine di visualizzazione di alcuni elenchi",
      "Rivisto il modo in cui vengono presentati gli articoli in magazzino durante le fasi di consegna/scarico",
      "Corretto un problema nel magazzino in cui in alcuni casi erronemanete salvava un articolo con un nome diverso da quello previsto",
      "Corretto un problema che impediva la visualizzazione degli sfondi colorati nei pdf generati durante la stampa",
      "Corretto un problema in cui in un caso particolare il sezionale di una fattura veniva rimosso",
      "Corretto un problema di layout nel lightsearch",
      "Corretto un problema nei reminder",
      "Corretti alcuni problemi inerenti le traduzioni",
      "Corretto un problema che in alcuni casi impediva di salvare la configurazione dell'account",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1f4dee34055f9f23da357656f72518da4f5e2c9f",
    committedAt: 1744616891,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e9f62c1d3c391a6582cd2e669851d900c6201233",
    committedAt: 1744616891,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "09ef0f47726214338642f685974f3f89bb32578b",
    committedAt: 1744365989,
    subject: "fix(SNT-8268): quote totalAdvances when minMax enabled",
    version: null,
    bullets: [
      "Corretto un problema nella visualizzazione del totale degli acconti in un preventivo quando minimo/massimo è abilitato"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "7514f65da1c015f935f7e9b13d759053aa3092f5",
    committedAt: 1744365989,
    subject: "fix(SNT-8268): quote totalAdvances when minMax enabled",
    version: null,
    bullets: [
      "Corretto un problema nella visualizzazione del totale degli acconti in un preventivo quando minimo/massimo è abilitato"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "c9600814efb12d9f1abbecfda14a450bdbe8d563",
    committedAt: 1744116846,
    subject: "fix(SNT-8226): remove adjustedTotal calc",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0b5589a891c03b3a7c9bac60dc8222f59095e09b",
    committedAt: 1744116846,
    subject: "fix(SNT-8226): remove adjustedTotal calc",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "76ccbb5802e101ca573bc5bc6e40e986db979641",
    committedAt: 1744020075,
    subject: "fix(SNT-8202): remove  maxAdjustedTotal",
    version: null,
    bullets: [
      "Corretto un problema che impediva la corretta visualizzazione del totale del preventivo in caso di min/max abilitato"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "536533d00b29268b849d845f36285ea563419607",
    committedAt: 1744020075,
    subject: "fix(SNT-8202): remove  maxAdjustedTotal",
    version: null,
    bullets: [
      "Corretto un problema che impediva la corretta visualizzazione del totale del preventivo in caso di min/max abilitato"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b9d202a42446bd06f2ffa637bee75882783b4f2c",
    committedAt: 1743536111,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1a017d65d543fe12d6ed6eecad9be49b3280b2eb",
    committedAt: 1743536111,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "f5eeed44477f663f1aed347e3266fd45abfa521e",
    committedAt: 1743535518,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Aggiunto il componente aggiuntivo per la comunicazione con VetInfo REV",
      "Aggiunte le note di default delle fatture",
      "Ora i risultati della light search sono in ordine alfabetico",
      "Migliorata l'estrazione dei contatti in maniera massiva",
      "Corretto un problema nell'aggiornamento dei contatori delle fatture",
      "Corretto un problema relativo alla soddisfazione clienti",
      "Corretto un problema durante la creazione dei vaccini",
      "Corretto un problema relativo al conto paziente di un paziente appena creato",
      "Corretto un problema che impediva la modifica del nome di una scheda clinica",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "7ac31edfaea22dd92269132366fb9c95d52f9179",
    committedAt: 1743535518,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Aggiunto il componente aggiuntivo per la comunicazione con VetInfo REV",
      "Aggiunte le note di default delle fatture",
      "Ora i risultati della light search sono in ordine alfabetico",
      "Migliorata l'estrazione dei contatti in maniera massiva",
      "Corretto un problema nell'aggiornamento dei contatori delle fatture",
      "Corretto un problema relativo alla soddisfazione clienti",
      "Corretto un problema durante la creazione dei vaccini",
      "Corretto un problema relativo al conto paziente di un paziente appena creato",
      "Corretto un problema che impediva la modifica del nome di una scheda clinica",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1400eac59bfb809366f69cba8a2058126f529d34",
    committedAt: 1742834012,
    subject: "fix(SNT-8127): limit changeCurrentDate() in scheduler",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "b8d03419fe782e489574ab3a02c5bc75c7992af1",
    committedAt: 1742834012,
    subject: "fix(SNT-8127): limit changeCurrentDate() in scheduler",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "599c3a4f09d6c5cf5eaf9591009b3d85d41a37f1",
    committedAt: 1742376952,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Corretto un problema che impediva l|importazione massiva di un tariffario tramite file Excel"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "35aba817635c1f1c359c4c29ada81393120c2907",
    committedAt: 1742376952,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Corretto un problema che impediva l|importazione massiva di un tariffario tramite file Excel"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "2f4e38b601d39c35329d521f0207f450a86a52fd",
    committedAt: 1741418315,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "6e763cc906c3fe098cdbf11b90f5b6ded892c4cf",
    committedAt: 1741418315,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1bcb24cdbf74b4a8cc0869db3ef4265e0b18b4da",
    committedAt: 1741182637,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "dcb6d081c68edf6205a924e712c79ee1b41dc954",
    committedAt: 1741182637,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a84c90890fabb901679a16d1c548729351734949",
    committedAt: 1741100157,
    subject: "feat(SNT-1): add changelog video",
    version: null,
    bullets: ["Aggiunta l'archiviazione a lungo termine degli studi"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "0568a7accddef0816ccb6ec7d05a25e304a360c1",
    committedAt: 1741100157,
    subject: "feat(SNT-1): add changelog video",
    version: null,
    bullets: ["Aggiunta l'archiviazione a lungo termine degli studi"]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "6ad50db8fc63017efb82e926976379060f6c6863",
    committedAt: 1741018445,
    subject: "feat(SNT-7321): idexx create connection",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a43bb70d0fa8000e868c5c085258e11a6a72ae09",
    committedAt: 1741018445,
    subject: "feat(SNT-7321): idexx create connection",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "3c43d517b54f4900fde08a19fd758cc3871ab1a9",
    committedAt: 1740731871,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e941c99581364844580afdf51e9cee38f776b2dd",
    committedAt: 1740731871,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "8b046ee9526778b664848c5ebb3c9ac3db828c51",
    committedAt: 1740672027,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "04e8104b8b0200610af51803846b00ecaae0628c",
    committedAt: 1740672027,
    subject: "feat(SNT-1): update changelog",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "be1a91b75996dde83cc968ffe2ecaf31267cddb6",
    committedAt: 1739975475,
    subject: "fix(SNT-7930): inject template appointment reminder",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "82636bce88c7ca6cbf56afc1595896c5e5130f35",
    committedAt: 1739975475,
    subject: "fix(SNT-7930): inject template appointment reminder",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "950aa98d76cdaa97e6447c77895d2186c28f7b72",
    committedAt: 1739885337,
    subject: "fix(SNT-7915): readonly advance-invoice",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "56896d9831dd611d61db32a295f982bc065ff46b",
    committedAt: 1739885337,
    subject: "fix(SNT-7915): readonly advance-invoice",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "d6f92805406c05cc4872ca807a5bf935c2a31d39",
    committedAt: 1739878366,
    subject: "fix(SNT-7914): bad total invoice amount",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "6c37eeb778f950118b1a6e53aa301e238c908564",
    committedAt: 1739878366,
    subject: "fix(SNT-7914): bad total invoice amount",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "1df25afc4399d7957e8671badf5281c90872ee84",
    committedAt: 1739198642,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Corretto un problema che impediva la visualizzazione da smartphone"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "f9c11b407d95c87ed2dff8840efd6aed86e78a46",
    committedAt: 1739198642,
    subject: "feat(SNT-1): changelog",
    version: null,
    bullets: [
      "Corretto un problema che impediva la visualizzazione da smartphone"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a952526cf7ff672ad41b3d8dd8970f686b7fb0d2",
    committedAt: 1738076871,
    subject: "feat(SNT-1): fix",
    version: null,
    bullets: [
      "Aggiunte le statistiche sugli studi",
      "Aggiunta la possibilità di filtrare gli studi in base alla presenza della richiesta",
      "Aggiunta la possibilità di esportare gli studi",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "65be35fd20e1f36063f0dcc398758c2b2c4a5fba",
    committedAt: 1738076871,
    subject: "feat(SNT-1): fix",
    version: null,
    bullets: [
      "Aggiunte le statistiche sugli studi",
      "Aggiunta la possibilità di filtrare gli studi in base alla presenza della richiesta",
      "Aggiunta la possibilità di esportare gli studi",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "a4bdddcdd2b84eaa5c13c3cc713e23e8d50d112f",
    committedAt: 1737736641,
    subject: "feat(SNT-1): RC",
    version: null,
    bullets: [
      "Inizio del supporto di integrazioni verso servizi esterni",
      "Aggiunta la possibilità di modificare la dimensione dei loghi nella carta intestata",
      "Aggiunto il nome della tipologia di esame nella stampa dei referti di laboratorio",
      "Corretto un problema di visualizzazione nell'agenda",
      "Aggiunta la possibilità di importare fatture passive segnate come già scaricate su FTPA",
      "Aggiunto il nome del cliente sui preventivi firmati",
      "Migliorato il layout degli elementi in evidenza nei record del diario clinico",
      "Aggiunto il peso ed il microchip del paziente nella stampa dei preventivi",
      "Corretto un problema con la creazione di appuntamenti verso clienti di tipo 'Pubblica amministrazione'",
      "Corretto un problema nei report delle statistiche",
      "Corretto un problema che impediva il caricamento delle degenze",
      "Corretto un problema che impediva la corretta sincronizzazione della flowboard",
      "Corretto un problema che impediva il corretto riposizionamento degli appuntamenti in agenda dopo un ridimensionamento",
      "Corretto un problema che mostrava una lista infinita di modelli di preventivo",
      "Aggiornate le traduzioni in inglese",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e2b7d6d8114e43a18dfb371fe174fa61f47ac73b",
    committedAt: 1737736641,
    subject: "feat(SNT-1): RC",
    version: null,
    bullets: [
      "Inizio del supporto di integrazioni verso servizi esterni",
      "Aggiunta la possibilità di modificare la dimensione dei loghi nella carta intestata",
      "Aggiunto il nome della tipologia di esame nella stampa dei referti di laboratorio",
      "Corretto un problema di visualizzazione nell'agenda",
      "Aggiunta la possibilità di importare fatture passive segnate come già scaricate su FTPA",
      "Aggiunto il nome del cliente sui preventivi firmati",
      "Migliorato il layout degli elementi in evidenza nei record del diario clinico",
      "Aggiunto il peso ed il microchip del paziente nella stampa dei preventivi",
      "Corretto un problema con la creazione di appuntamenti verso clienti di tipo 'Pubblica amministrazione'",
      "Corretto un problema nei report delle statistiche",
      "Corretto un problema che impediva il caricamento delle degenze",
      "Corretto un problema che impediva la corretta sincronizzazione della flowboard",
      "Corretto un problema che impediva il corretto riposizionamento degli appuntamenti in agenda dopo un ridimensionamento",
      "Corretto un problema che mostrava una lista infinita di modelli di preventivo",
      "Aggiornate le traduzioni in inglese",
      "Correzioni minori"
    ]
  },
  {
    product: SoftwareProduct.PMS,
    sha: "e4693fabcc803e7f5aee23bd3468693aa3c3510a",
    committedAt: 1737643572,
    subject: "fix(SNT-1): fix",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "24918e670f3b4da8ff57585fefc044edf1cda0e8",
    committedAt: 1737643572,
    subject: "fix(SNT-1): fix",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "53967fe213139ec33b8e4d2babaa353c2b9549ec",
    committedAt: 1737545358,
    subject: "fix(SNT-1): fix",
    version: null,
    bullets: []
  },
  {
    product: SoftwareProduct.PMS,
    sha: "9117c0efbbb5a7307410f55abb55e733b13da7eb",
    committedAt: 1737545358,
    subject: "fix(SNT-1): fix",
    version: null,
    bullets: []
  }
];

@Injectable({
  providedIn: "root"
})
export class ChangelogHistoryService {
  readonly #entries: readonly ChangelogHistoryEntry[];

  readonly #semverMaxEntry = (
    entries: readonly ChangelogHistoryEntry[]
  ): ChangelogHistoryEntry =>
    [...entries].sort(
      (a: ChangelogHistoryEntry, b: ChangelogHistoryEntry): number => {
        const av: semver.SemVer | null = semver.coerce(a.version ?? "");
        const bv: semver.SemVer | null = semver.coerce(b.version ?? "");

        if (av && bv) {
          return semver.rcompare(av, bv);
        }

        return b.committedAt - a.committedAt;
      }
    )[0];

  constructor() {
    const raw: ChangelogHistoryEntry[] = [...CHANGELOG_HISTORY_SOURCE];

    this.#entries = [...raw].sort(
      (a: ChangelogHistoryEntry, b: ChangelogHistoryEntry) => {
        return b.committedAt - a.committedAt;
      }
    );
  }

  getAllEntries(): readonly ChangelogHistoryEntry[] {
    return this.#entries;
  }

  getAllEntriesByProduct(
    product: SoftwareProduct
  ): readonly ChangelogHistoryEntry[] {
    return this.#entries.filter(
      (entry: ChangelogHistoryEntry) => entry.product === product
    );
  }

  /**
   * Latest changelog row for the running product — chosen by semver vs the build
   * version ({@link getCurrentSoftwareProductVersion}), not by `committedAt` order
   * (which mixes products and breaks “newest embedded row” UX).
   */
  getLastChangelogByProduct(
    product: SoftwareProduct
  ): ChangelogHistoryEntry | null {
    const scoped: ChangelogHistoryEntry[] = this.#entries.filter(
      (entry: ChangelogHistoryEntry): boolean => entry.product === product
    );

    if (scoped.length === 0) {
      return null;
    }

    const versionedEntries: ChangelogHistoryEntry[] = scoped.filter(
      (entry: ChangelogHistoryEntry): boolean =>
        entry.version !== null &&
        semver.coerce(entry.version) !== null &&
        semver.valid(semver.coerce(entry.version)?.version ?? "") !== null
    );

    if (versionedEntries.length === 0) {
      const sortedByTime: ChangelogHistoryEntry[] = [...scoped].sort(
        (a: ChangelogHistoryEntry, b: ChangelogHistoryEntry): number =>
          b.committedAt - a.committedAt
      );

      return sortedByTime[0] ?? null;
    }

    const currentParsed: semver.SemVer | null = semver.coerce(
      getCurrentSoftwareProductVersion()
    );

    if (currentParsed !== null && semver.valid(currentParsed) !== null) {
      const current: string = currentParsed.version;

      const exactMatch: ChangelogHistoryEntry | undefined =
        versionedEntries.find((entry: ChangelogHistoryEntry): boolean => {
          const ev: semver.SemVer | null = semver.coerce(entry.version ?? "");

          return ev?.version === current;
        });

      if (exactMatch) {
        return exactMatch;
      }

      const atOrBelowCurrent: ChangelogHistoryEntry[] = versionedEntries.filter(
        (entry: ChangelogHistoryEntry): boolean => {
          const ev: semver.SemVer | null = semver.coerce(entry.version ?? "");

          if (ev === null) {
            return false;
          }

          return semver.lte(ev, currentParsed);
        }
      );

      if (atOrBelowCurrent.length > 0) {
        return this.#semverMaxEntry(atOrBelowCurrent);
      }
    }

    return this.#semverMaxEntry(versionedEntries);
  }
}
